import { useAuthData } from "../contexts";
import { useSetRecoilState } from "recoil";
import {
    ActiveSchoolOrCompanyAtom,
    MessageType,
    NewThreadMessagesAtom,
    UserCompanyListAtom,
    UserSchoolListAtom,
} from "../atoms";
import { useAddMessage } from "./useAddMessage";
import {
    Company,
    CompanyApi,
    MessageThread,
    MessageThreadApi,
    School,
    SchoolApi,
    UserApi,
} from "../apis";
import { APPC, LSC } from "../config";

type AuthActionType = {
    loadSchoolAndCompanyAction: () => Promise<void>;
    loadMessageIdsAction: () => Promise<void>;
    loadNewMessageThreadsAction: () => Promise<void>;
};

export function useInitLoadAction(): AuthActionType {
    const { grant } = useAuthData();
    const setUserSchoolList = useSetRecoilState(UserSchoolListAtom);
    const setUserCompanyList = useSetRecoilState(UserCompanyListAtom);
    const setActiveSchoolOrCompany = useSetRecoilState(
        ActiveSchoolOrCompanyAtom
    );
    const { addMessage } = useAddMessage();
    const setNewThreadMessages = useSetRecoilState(NewThreadMessagesAtom);

    const loadSchoolAndCompanyAction = async () => {
        let schoolOrCompany: School | Company | null = null;
        if (grant.hasCompanyAccess) {
            const { response: companyResponse } =
                await CompanyApi.getCollectionMyCompanies<Company>(1, {
                    [`order[name]`]: "asc",
                    pagination: false,
                });

            if (companyResponse && companyResponse?.totalItems > 0) {
                setUserCompanyList(companyResponse.items);
                companyResponse.items.forEach((c) => {
                    if (
                        CompanyApi.toResourceIRI(c.id) ===
                        localStorage.getItem(LSC.APP_SEL_SC)
                    ) {
                        schoolOrCompany = c;
                    }
                });
                if (!schoolOrCompany && !grant.hasSchoolAccess) {
                    schoolOrCompany = companyResponse.items[0];
                }
            }
        }
        if (grant.hasSchoolAccess) {
            const { response: schoolResponse } =
                await SchoolApi.getCollectionMySchools<School>(1, {
                    [`order[name]`]: "asc",
                    pagination: false,
                });

            if (schoolResponse && schoolResponse?.totalItems > 0) {
                setUserSchoolList(schoolResponse.items);
                schoolResponse.items.forEach((c) => {
                    if (
                        SchoolApi.toResourceIRI(c.id) ===
                        localStorage.getItem(LSC.APP_SEL_SC)
                    ) {
                        schoolOrCompany = c;
                    }
                });
                if (!schoolOrCompany) {
                    schoolOrCompany = schoolResponse.items[0];
                }
            }
        }

        if (schoolOrCompany) {
            setActiveSchoolOrCompany(schoolOrCompany);
            localStorage.setItem(LSC.APP_SEL_SC, schoolOrCompany?.["@id"]);
        }
    };

    const loadMessageIdsAction = async () => {
        const { response } =
            await UserApi.getCollectionUserMessage<MessageType>();
        if (response && response?.items?.length > 0) {
            addMessage(response.items);
        }
    };

    const loadNewMessageThreadsAction = async () => {
        const { response: responseO2O } =
            await MessageThreadApi.getCollectionNewMessageThreads<MessageThread>(
                1,
                { messageThreadType: APPC.BE.MessageThread.TYPE.TYPE_O2O }
            );
        const { response: responseInternship } =
            await MessageThreadApi.getCollectionNewMessageThreads<MessageThread>(
                1,
                {
                    messageThreadType:
                        APPC.BE.MessageThread.TYPE.TYPE_INTERNSHIP,
                }
            );
        const response = [
            ...(responseO2O?.items || []),
            ...(responseInternship?.items || []),
        ];
        if (response && response?.length > 0) {
            setNewThreadMessages(response.map((t) => t.id));
        }
    };

    return {
        loadSchoolAndCompanyAction,
        loadMessageIdsAction,
        loadNewMessageThreadsAction,
    };
}
